// Shared mention/tag plumbing for the Agentforge composer and message
// renderer. Tags travel over the wire as <tag id="X" entity="Y">name</tag>
// markers embedded in otherwise-plain text; the editor stores them as
// `contenteditable=false` chip spans so the user can backspace them as
// single atomic units instead of character-by-character.
//
// The same Token shape powers both directions:
//   - editor → server: walk the live DOM, emit text + <tag> markers
//   - server → display / editor: parse markers into tokens, then either
//     React-render them (Message) or seed contenteditable nodes (Composer)

export interface MentionMeta {
  id: string;
  entity: string;
  name: string;
}

export type MentionToken =
  | { type: 'text'; text: string }
  | { type: 'mention'; mention: MentionMeta };

// Matches `<tag id=… entity=…>name</tag>`. Tolerant of both quoted
// (`id="12"`) and bare (`id=12`) attribute forms so historical server
// payloads keep parsing after we switched the emitter to bare attrs.
// Capture order: id-quoted, id-bare, entity-quoted, entity-bare, name.
const TAG_REGEX =
  /<tag\s+id=(?:"([^"]*)"|([^\s>]+))\s+entity=(?:"([^"]*)"|([^\s>]+))>([^<]*)<\/tag>/g;

export const MENTION_CHIP_CLASS = 'agentforge-mention-chip';

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const unescapeHtml = (text: string): string =>
  text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

// Bare attribute values can't contain whitespace, `>`, or `"` without
// breaking the parser. The fixtures only ever produce alphanumerics +
// hyphens / underscores, but we sanitize defensively so a hand-crafted
// suggestion can't malform the wire payload.
const sanitizeBareAttr = (text: string): string =>
  text.replace(/[\s">]+/g, '_');

export const formatMentionTag = (m: MentionMeta): string =>
  `<tag id=${sanitizeBareAttr(m.id)} entity=${sanitizeBareAttr(
    m.entity
  )}>${escapeHtml(m.name)}</tag>`;

// Server string → ordered tokens. Text segments are HTML-unescaped so the
// editor / renderer sees the original characters. We use a fresh RegExp
// each call (TAG_REGEX is stateful via /g).
export const parseServerContent = (content: string): MentionToken[] => {
  if (!content) return [];
  const tokens: MentionToken[] = [];
  const regex = new RegExp(TAG_REGEX.source, 'g');
  let cursor = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > cursor) {
      tokens.push({
        type: 'text',
        text: unescapeHtml(content.slice(cursor, match.index)),
      });
    }
    tokens.push({
      type: 'mention',
      mention: {
        id: unescapeHtml(match[1] ?? match[2] ?? ''),
        entity: unescapeHtml(match[3] ?? match[4] ?? ''),
        name: unescapeHtml(match[5] ?? ''),
      },
    });
    cursor = match.index + match[0].length;
  }
  if (cursor < content.length) {
    tokens.push({ type: 'text', text: unescapeHtml(content.slice(cursor)) });
  }
  return tokens;
};

const readChipMeta = (el: HTMLElement): MentionMeta | null => {
  if (!el.classList.contains(MENTION_CHIP_CLASS)) return null;
  const id = el.dataset.mentionId;
  const entity = el.dataset.mentionEntity;
  const name = el.dataset.mentionName;
  if (id === undefined || entity === undefined || name === undefined) {
    return null;
  }
  return { id, entity, name };
};

interface WalkConfig {
  onText: (text: string) => void;
  onMention: (meta: MentionMeta) => void;
  onLineBreak: () => void;
}

// Depth-first walk that emits semantic events. The editor uses `<br>` for
// newlines and chip spans for mentions; both modes route through here so
// serialization stays consistent.
const walkEditorDom = (root: Node, cfg: WalkConfig): void => {
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      cfg.onText(node.textContent ?? '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.tagName === 'BR') {
      cfg.onLineBreak();
      return;
    }
    const meta = readChipMeta(el);
    if (meta) {
      cfg.onMention(meta);
      return;
    }
    // contenteditable can wrap newlines in <div> / <p>. Treat the boundary
    // between siblings as a newline so paste / Enter still round-trip.
    if (el.tagName === 'DIV' || el.tagName === 'P') {
      // Only insert a leading newline when we already have content — the
      // very first <div> is just the wrapper, not a hard break.
      cfg.onLineBreak();
    }
    walkEditorDom(el, cfg);
  });
};

export const serializeDom = (root: HTMLElement): string => {
  const parts: string[] = [];
  let isFirstBlock = true;
  walkEditorDom(root, {
    onText: (text) => parts.push(escapeHtml(text)),
    onMention: (meta) => parts.push(formatMentionTag(meta)),
    onLineBreak: () => {
      if (isFirstBlock && parts.length === 0) {
        // Suppress the synthetic newline a leading wrapper <div> would emit
        // — the editor's own contents start at column 0.
        isFirstBlock = false;
        return;
      }
      isFirstBlock = false;
      parts.push('\n');
    },
  });
  return parts.join('');
};

export const plainTextFromDom = (root: HTMLElement): string => {
  const parts: string[] = [];
  let isFirstBlock = true;
  walkEditorDom(root, {
    onText: (text) => parts.push(text),
    onMention: (meta) => parts.push(`@${meta.name}`),
    onLineBreak: () => {
      if (isFirstBlock && parts.length === 0) {
        isFirstBlock = false;
        return;
      }
      isFirstBlock = false;
      parts.push('\n');
    },
  });
  return parts.join('');
};
